import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EmailLogsService, EmailLog, PaginationLink } from '../../services/email-logs.service';
import { CommonService } from '../../services/common.service';
import { DynamicDataModalComponent } from '../dynamic-data-modal/dynamic-data-modal.component';
import { MandrillEmailServiceService } from '../../../customer-email/services/mandrill-email-service.service';

@Component({
  selector: 'app-email-logs',
  templateUrl: './email-logs.component.html',
  styleUrls: ['./email-logs.component.scss']
})
export class EmailLogsComponent implements OnInit {
  @Input() registrationId?: string;
  @Input() customerId?: string;
  
  isLoading: boolean = false;
  emailLogs: EmailLog[] = [];
  totalRecords: number = 0;
  currentPage: number = 1; 
  perPage: number = 10;
  lastPage: number = 1;
  paginationLinks: PaginationLink[] = [];
  searchTerm: string = '';
  selectedLogs: Set<number> = new Set();
  allSelected: boolean = false;
  Math = Math; // Make Math available in template
  
  // Mandrill preview properties
  mandrillLoading: boolean = false;
  mandrillEmailContent: string = '';
  mandrillError: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private emailLogsService: EmailLogsService,
    private commonService: CommonService,
    private mandrillEmailService: MandrillEmailServiceService
  ) {}

  ngOnInit(): void {
    // If customerId is provided, use it as search term
    if (this.customerId) {
      this.searchTerm = this.customerId;
      this.loadEmailLogs(1); // Load first page with customerId as search term
    } else if (this.registrationId) {
      this.searchTerm = this.registrationId;
      this.loadEmailLogs(1);
    } else {
      this.loadEmailLogs(); // Load without search term if no IDs provided
    }
  }

  onSearch(): void {
    this.currentPage = 1; // Reset to first page when searching
    this.loadEmailLogs(1);
  }

  loadEmailLogs(page: number = 1): void {
    this.isLoading = true;
    this.currentPage = page;
    // Clear selections when loading new data
    this.selectedLogs.clear();
    this.allSelected = false;

    const params = {
      page: page,
      per_page: this.perPage,
      searchTerm: this.searchTerm || undefined,
      isCustomerId: this.customerId ? true : false // Add flag to indicate if searching by customerId
    };

    this.emailLogsService.getReminderEmailLogs(params).subscribe({
      next: (response) => {
        if (response.Status) {
          this.emailLogs = response.data.data;
          this.totalRecords = response.data.total;
          this.currentPage = response.data.current_page;
          this.lastPage = response.data.last_page;
          this.paginationLinks = response.data.links;
        } else {
          // Only show error toast if not in customerId mode
          if (!this.customerId) {
            this.commonService.showToast('error', 'Error', response.message || 'Failed to fetch email logs');
          }
        }
      },
      error: (error) => {
        console.error('Error fetching email logs:', error);
        // Only show error toast if not in customerId mode
        if (!this.customerId) {
          this.commonService.showToast('error', 'Error', 'Failed to fetch email logs');
        }
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  onPageChange(url: string | null): void {
    if (!url) return;
    
    const pageMatch = url.match(/page=(\d+)/);
    if (pageMatch && pageMatch[1]) {
      const page = parseInt(pageMatch[1], 10);
      this.loadEmailLogs(page);
    }
  }

  getDynamicData(log: EmailLog): any {
    console.log('getDynamicData called with log.DynamicData:', log.DynamicData);
    
    // Check if DynamicData is null, undefined, or empty
    if (!log.DynamicData || log.DynamicData.trim() === '') {
      console.log('DynamicData is empty or null');
      return [];
    }

    try {
      const parsed = JSON.parse(log.DynamicData);
      console.log('Parsed DynamicData:', parsed);
      
      // If it's already an array with name/content structure, return as is
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].hasOwnProperty('name') && parsed[0].hasOwnProperty('content')) {
        console.log('Data is already in correct format');
        return parsed;
      }
      
      // If it's a plain object, convert it to array format
      if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null) {
        console.log('Converting object to array format');
        const converted = Object.keys(parsed).map(key => ({
          name: key,
          content: parsed[key] !== null && parsed[key] !== undefined ? String(parsed[key]) : 'N/A'
        }));
        console.log('Converted data:', converted);
        return converted;
      }
      
      // If it's a string or other type, try to create a single item array
      if (typeof parsed === 'string' || typeof parsed === 'number') {
        console.log('Creating single item array for primitive value');
        return [{
          name: 'Value',
          content: String(parsed)
        }];
      }
      
      console.log('Returning parsed data as-is:', parsed);
      return parsed;
    } catch (e) {
      console.error('Error parsing dynamic data:', e);
      console.log('Attempting to treat as plain text');
      
      // If JSON parsing fails, treat as plain text
      return [{
        name: 'Raw Data',
        content: log.DynamicData
      }];
    }
  }

  getRecipientName(log: EmailLog): string {
    const data = this.getDynamicData(log);
    
    // Handle array format
    if (Array.isArray(data)) {
      const fnameItem = data.find(item => item.name === 'FNAME');
      return fnameItem ? fnameItem.content : 'N/A';
    }
    
    // Handle object format (fallback)
    return data.FNAME || 'N/A';
  }

  getEnrollmentCode(log: EmailLog): string {
    const data = this.getDynamicData(log);
    
    // Handle array format
    if (Array.isArray(data)) {
      const enrollmentItem = data.find(item => item.name === 'ENROLMENT_CODE');
      return enrollmentItem ? enrollmentItem.content : 'N/A';
    }
    
    // Handle object format (fallback)
    return data.ENROLMENT_CODE || 'N/A';
  }

  getPendingDocuments(log: EmailLog): string {
    const data = this.getDynamicData(log);
    
    // Handle array format
    if (Array.isArray(data)) {
      const pendingItem = data.find(item => item.name === 'PENDING_DOCUMENT_LIST');
      return pendingItem ? pendingItem.content : 'N/A';
    }
    
    // Handle object format (fallback)
    return data.PENDING_DOCUMENT_LIST || 'N/A';
  }

  getProgressPercent(log: EmailLog): string {
    const data = this.getDynamicData(log);
    
    // Handle array format
    if (Array.isArray(data)) {
      const progressItem = data.find(item => item.name === 'PROGRESS_PERCENT');
      return progressItem && progressItem.content ? `${progressItem.content}%` : 'N/A';
    }
    
    // Handle object format (fallback)
    return data.PROGRESS_PERCENT ? `${data.PROGRESS_PERCENT}%` : 'N/A';
  }

  toggleSelectAll(): void {
    this.allSelected = !this.allSelected;
    if (this.allSelected) {
      this.emailLogs.forEach(log => this.selectedLogs.add(log.id));
    } else {
      this.selectedLogs.clear();
    }
  }

  toggleSelectLog(logId: number): void {
    if (this.selectedLogs.has(logId)) {
      this.selectedLogs.delete(logId);
      this.allSelected = false;
    } else {
      this.selectedLogs.add(logId);
      this.allSelected = this.selectedLogs.size === this.emailLogs.length;
    }
  }

  isSelected(logId: number): boolean {
    return this.selectedLogs.has(logId);
  }

  get itemsRange(): { start: number; end: number } {
    const start = (this.currentPage - 1) * this.perPage + 1;
    const end = Math.min(this.currentPage * this.perPage, this.totalRecords);
    return { start, end };
  }

  // Generate pagination range with ellipsis
  getPaginationRange(): (number | string)[] {
    const range: (number | string)[] = [];
    const totalPages = this.lastPage;
    const current = this.currentPage;
    
    // Always show first page
    range.push(1);
    
    if (totalPages <= 4) {
      // If total pages are 4 or less, show all pages
      for (let i = 2; i <= totalPages; i++) {
        range.push(i);
      }
    } else {
      if (current <= 3) {
        // Near the start
        range.push(2, 3);
        if (current + 1 < totalPages) {
          range.push('...');
        }
        if (totalPages > 3) {
          range.push(totalPages);
        }
      } else if (current >= totalPages - 2) {
        // Near the end
        range.push('...');
        range.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Middle - show current page, one before and one after
        range.push('...');
        range.push(current - 1, current, current + 1);
        if (current + 2 < totalPages) {
          range.push('...');
        }
        range.push(totalPages);
      }
    }
    
    return range;
  }

  showDynamicData(log: EmailLog): void {
    try {
      console.log('showDynamicData called with log:', log);
      console.log('Raw DynamicData:', log.DynamicData);
      
      const dynamicData = this.getDynamicData(log);
      console.log('Processed dynamic data:', dynamicData);
      
      let formattedData: any[];

      // If it's already in the correct array format, use it directly
      if (Array.isArray(dynamicData) && dynamicData.length > 0 && 
          dynamicData[0].hasOwnProperty('name') && dynamicData[0].hasOwnProperty('content')) {
        formattedData = dynamicData;
        console.log('Using data as-is (already in correct format):', formattedData);
      } else {
        // If it's in object format or needs conversion, the getDynamicData method already handles this
        formattedData = dynamicData;
        console.log('Using processed data:', formattedData);
      }

      console.log('Final data being passed to modal:', formattedData);

      // Create a modal dialog to display the data
      const modalRef = this.modalService.open(DynamicDataModalComponent, {
        size: 'lg',
        centered: true
      });
      modalRef.componentInstance.data = formattedData;
      modalRef.componentInstance.email = log.Email;
      
      console.log('Modal opened with data:', formattedData, 'and email:', log.Email);
    } catch (error) {
      console.error('Error processing dynamic data:', error);
      this.commonService.showToast('error', 'Error', 'Failed to process dynamic data');
    }
  }

  showMandrillPreview(messageId: string): void {
    if (!messageId) {
      this.commonService.showToast('error', 'Error', 'Message ID is required');
      return;
    }

    console.log('showMandrillPreview called with messageId:', messageId);
    
    // Reset modal state
    this.mandrillLoading = true;
    this.mandrillEmailContent = '';
    this.mandrillError = '';

    // Open the modal with simple configuration
    const modalElement = document.getElementById('mandrillPreviewModal');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }

    // Call the API
    const payload = {
      emailId: messageId
    };

    console.log('Calling getMandrillEmailPreview API with payload:', payload);

    this.mandrillEmailService.getMandrillEmailPreview(payload).subscribe({
      next: (response) => {
        console.log('Mandrill API response:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', response ? Object.keys(response) : 'No response');
        this.mandrillLoading = false;
        
        // Check different possible response structures
        if (response) {
          let emailData = null;
          
          // Case 1: Response has Status property and data
          if (response.Status && response.data) {
            emailData = response.data;
            console.log('Using response.data:', emailData);
          }
          // Case 2: Response itself is the email data (direct response)
          else if (response.html || response.subject || response.from_email) {
            emailData = response;
            console.log('Using response directly as email data:', emailData);
          }
          // Case 3: Response has a different success indicator
          else if (response.status === 'success' || response.success) {
            emailData = response.data || response;
            console.log('Using success response data:', emailData);
          }
          // Case 4: Try to use response as-is if it has email-like properties
          else if (response.to || response.email || response._id) {
            emailData = response;
            console.log('Using response as email data based on properties:', emailData);
          }
          
          if (emailData) {
            console.log('Processing email data:', emailData);
            
            // Extract HTML content from the email data
            this.mandrillEmailContent = emailData.html || emailData.content || '';
            console.log('HTML content found:', !!this.mandrillEmailContent);
            
            // If no HTML content, try to construct a preview from available data
            if (!this.mandrillEmailContent && emailData.text) {
              this.mandrillEmailContent = `<pre>${emailData.text}</pre>`;
              console.log('Using text content as fallback');
            }
            
            // If still no content, show email metadata
            if (!this.mandrillEmailContent) {
              this.mandrillEmailContent = this.generateEmailMetadataPreview(emailData);
              console.log('Generated metadata preview');
            }
            
            console.log('Final content length:', this.mandrillEmailContent.length);
          } else {
            console.log('No valid email data found in response');
            this.mandrillError = 'No email data available in response';
          }
        } else {
          console.log('No response received');
          this.mandrillError = 'No response received from server';
        }
      },
      error: (error) => {
        console.error('Error fetching Mandrill email preview:', error);
        this.mandrillLoading = false;
        this.mandrillError = 'Failed to load email preview. Please try again.';
      }
    });
  }

  private generateEmailMetadataPreview(emailData: any): string {
    const metadata = [];
    
    if (emailData.subject) {
      metadata.push(`<h4>Subject: ${emailData.subject}</h4>`);
    }
    
    if (emailData.from_email) {
      const fromName = emailData.from_name ? `${emailData.from_name} <${emailData.from_email}>` : emailData.from_email;
      metadata.push(`<p><strong>From:</strong> ${fromName}</p>`);
    }
    
    if (emailData.to && Array.isArray(emailData.to)) {
      const toList = emailData.to.map((recipient: any) => {
        return recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;
      }).join(', ');
      metadata.push(`<p><strong>To:</strong> ${toList}</p>`);
    } else if (emailData.email) {
      const toName = emailData.name ? `${emailData.name} <${emailData.email}>` : emailData.email;
      metadata.push(`<p><strong>To:</strong> ${toName}</p>`);
    }
    
    if (emailData.ts) {
      const date = new Date(emailData.ts * 1000).toLocaleString();
      metadata.push(`<p><strong>Date:</strong> ${date}</p>`);
    }
    
    if (emailData.tags && emailData.tags.length > 0) {
      metadata.push(`<p><strong>Tags:</strong> ${emailData.tags.join(', ')}</p>`);
    }
    
    if (emailData.text) {
      metadata.push(`<hr><h5>Text Content:</h5><pre style="white-space: pre-wrap; background: #f8f9fa; padding: 15px; border-radius: 4px;">${emailData.text}</pre>`);
    }
    
    if (emailData.attachments && emailData.attachments.length > 0) {
      metadata.push(`<hr><h5>Attachments:</h5><ul>${emailData.attachments.map((att: any) => `<li>${att.name || 'Attachment'}</li>`).join('')}</ul>`);
    }
    
    return `
      <div style="font-family: Arial, sans-serif; line-height: 1.6;">
        ${metadata.join('')}
        <hr>
        <p><em>No HTML content available for this email.</em></p>
      </div>
    `;
  }
}

// Service method to open the modal
export class YourService {
  constructor(private modalService: NgbModal) {}

  openEmailLogs(registrationId?: string) {
    const modalRef = this.modalService.open(EmailLogsComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-fullscreen-xl-down modal-dialog-centered',
      windowClass: 'email-logs-modal'
    });
    
    if (registrationId) {
      modalRef.componentInstance.registrationId = registrationId;
    }
    
    return modalRef.result;
  }
} 